import type { AgeBand } from './phrases';

/**
 * Background noise during practice, handled kindly and sparingly. Children practise where they
 * are; many have no quiet room to go to, so the helper asks instead of telling, accepts "no",
 * and never blames the child for tries the noise spoiled.
 *
 * - Noise is judged from what the helper can measure: tries it could not judge in a row (heard
 *   but drowned, too quiet against the background) and a steady noise floor well above a quiet
 *   room's.
 * - First time: the helper says what it hears and asks whether a quieter spot is available (for
 *   younger children: a quiet room, with a grown-up's help). The child answers yes (the
 *   microphone pauses until they are there) or no (practice goes on).
 * - At most one follow-up, a while later and only if the noise is still there: after "yes", is
 *   there another spot, or keep going here; after "no", no question again, only the reassurance
 *   that missed tries are the noise, not them.
 * - Nothing more after that in the session.
 */

/** Tries in a row the helper could not judge before it counts as noise (the coach's old pause). */
export const UNJUDGED_FOR_NOISE = 4;
/**
 * A steady noise floor above this (dBFS, after the browser's level control) counts as noisy: a
 * quiet room with a laptop or iPhone microphone measures about -60 to -50 dBFS; a running washing
 * machine, a television or people talking nearby about -45 to -35 (estimated; QA level logs).
 */
export const NOISY_FLOOR_DB = -42;
/** How long the floor must stay above NOISY_FLOOR_DB to count. */
export const NOISY_FOR_MS = 15_000;
/** The follow-up comes no sooner than this after the first question. */
export const FOLLOW_UP_AFTER_MS = 120_000;

export type NoiseLine = 'noiseAsk' | 'noiseMoving' | 'noiseStay' | 'noiseFollowUp' | 'noiseStillOk';

/** What the helper does about the noise now. */
export interface NoiseAction {
  line: NoiseLine;
  /** Ask the child (answer with `answer`): is a quieter spot available? */
  ask: boolean;
  /** Pause the microphone (the child is moving; they turn it back on when there). */
  pause: boolean;
}

export class NoiseCheck {
  private unjudged = 0;
  private loudSince: number | null = null;
  private askedAt: number | null = null;
  private answer_: 'moving' | 'stay' | null = null;
  private followedUp = false;
  private waiting = false;

  /** A try the helper judged (counts toward nothing; resets the run). */
  judged(): void {
    this.unjudged = 0;
  }

  /** A try the helper could not judge (not heard, too quiet against the background, ...). */
  unjudgedTry(nowMs: number): NoiseAction | null {
    this.unjudged++;
    if (this.unjudged < UNJUDGED_FOR_NOISE) return null;
    this.unjudged = 0;
    return this.noisy(nowMs);
  }

  /** The listener's steady noise floor (dBFS), about every second. */
  floor(db: number, nowMs: number): NoiseAction | null {
    if (db < NOISY_FLOOR_DB) {
      this.loudSince = null;
      return null;
    }
    this.loudSince ??= nowMs;
    if (nowMs - this.loudSince < NOISY_FOR_MS) return null;
    this.loudSince = null;
    return this.noisy(nowMs);
  }

  /** Waiting for the child's answer to the question. */
  get asking(): boolean {
    return this.waiting;
  }

  /** The child's answer: a quieter spot is available (they go there) or not (keep going). */
  answer(moving: boolean, nowMs: number): NoiseAction | null {
    if (!this.waiting) return null;
    this.waiting = false;
    this.answer_ = moving ? 'moving' : 'stay';
    // The follow-up's wait counts from the answer (moving takes a while).
    this.askedAt = nowMs;
    return moving
      ? { line: 'noiseMoving', ask: false, pause: true }
      : { line: 'noiseStay', ask: false, pause: false };
  }

  private noisy(nowMs: number): NoiseAction | null {
    if (this.waiting || this.followedUp) return null;
    if (this.askedAt === null) {
      this.askedAt = nowMs;
      this.waiting = true;
      return { line: 'noiseAsk', ask: true, pause: false };
    }
    if (nowMs - this.askedAt < FOLLOW_UP_AFTER_MS) return null;
    this.followedUp = true;
    if (this.answer_ === 'stay') return { line: 'noiseStillOk', ask: false, pause: false };
    // Still noisy after moving: one more, last, question.
    this.waiting = true;
    return { line: 'noiseFollowUp', ask: true, pause: false };
  }
}

/** The lines, by age band (docs/research/frustration-handling.md: ask, don't diagnose). */
export const NOISE_LINES: Readonly<Record<NoiseLine, Readonly<Record<AgeBand, string>>>> = {
  noiseAsk: {
    younger:
      'I can hear some other sounds around you. Is there a quiet room you could go to? You can ask a grown-up to help.',
    older:
      'I’m hearing some background sounds, and they make it hard for me to hear you. Is there a quieter spot you could go to?',
  },
  noiseMoving: {
    younger: 'Great! Tap the microphone when you get there.',
    older: 'Great. Tap the microphone when you’re there.',
  },
  noiseStay: {
    younger: 'That’s okay! Let’s keep going. If I miss a try, it’s the noise, not you.',
    older: 'No problem, let’s keep going. If I miss a try, that’s the noise, not you.',
  },
  noiseFollowUp: {
    younger:
      'I can still hear sounds in the background. Is there another quiet place you could go to?',
    older:
      'I can still hear sounds in the background. Is there another quieter spot you could try?',
  },
  noiseStillOk: {
    younger: 'It’s still a bit noisy. That’s okay, you’re doing great. I might miss some tries.',
    older: 'Still a bit noisy here. I might miss a few tries, and that’s the noise, not you.',
  },
};

/** The answers to the question, tapped or said. */
export const NOISE_ANSWERS = {
  moving: { label: 'Yes, I’ll go there', says: ['yes', 'move', 'go there'] },
  stay: { label: 'No, keep going here', says: ['no', 'here', 'keep going', 'stay'] },
};
